// Donut chart of the customer's storage: burned to glass, waiting to be
// burned, and still free.
//
// Hand-drawn SVG rather than a charting dependency — three arcs on one circle
// do not justify shipping a library into a customer bundle.
//
// The chart is decorative in the accessibility sense: every figure it encodes
// is also present as text in the legend beside it, and the graphic itself
// carries a summary label, so nothing here is available to sighted users only.
import { useTranslation } from 'react-i18next';
import { formatBytes } from '../lib/format';
import { donutArcs, quotaChartModel } from '../lib/quotaChart';
import type { QuotaInput, QuotaSegmentKey } from '../lib/quotaChart';
import type { Language } from '../i18n';

const SIZE = 168;
const STROKE = 22;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const COLOR: Record<QuotaSegmentKey, string> = {
  burned: 'var(--accent)',
  staged: 'var(--warning)',
  free: 'var(--surface-2)',
};

export function StorageDonut({ quota, lang }: { quota: QuotaInput; lang: Language }) {
  const { t } = useTranslation();
  const model = quotaChartModel(quota);
  const arcs = donutArcs(model.segments.map((s) => s.fraction), CIRCUMFERENCE);

  if (model.empty) {
    return (
      <p className="muted" style={{ fontSize: 13, margin: 0 }}>
        {t('quota.chart.empty')}
      </p>
    );
  }

  const percent = Math.round(model.usedFraction * 100);
  // Over quota is the one state worth colouring differently — it changes what
  // the customer has to do next.
  const over = model.overBytes > 0;

  const summary = model.segments
    .filter((s) => s.bytes > 0)
    .map((s) => `${t(`quota.chart.${s.key}`)}: ${formatBytes(s.bytes, lang)}`)
    .join(', ');

  return (
    <div className="row" style={{ gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={`${t('quota.chart.title')} — ${summary}`}
        style={{ flex: '0 0 auto' }}
      >
        {/* Start at 12 o'clock and run clockwise, the direction a fill gauge is read. */}
        <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
          {model.segments.map((s, i) =>
            s.fraction > 0 ? (
              <circle
                key={s.key}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                strokeWidth={STROKE}
                stroke={s.key === 'burned' && over ? 'var(--danger)' : COLOR[s.key]}
                strokeDasharray={arcs[i].dashArray}
                strokeDashoffset={arcs[i].dashOffset}
              />
            ) : null,
          )}
        </g>
        {model.hasQuota ? (
          <>
            <text
              x="50%"
              y="48%"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: 26, fontWeight: 600, fill: over ? 'var(--danger)' : 'var(--text)' }}
            >
              {percent}%
            </text>
            <text x="50%" y="63%" textAnchor="middle" style={{ fontSize: 11, fill: 'var(--text-muted, #6b7280)' }}>
              {t('quota.chart.ofPurchased')}
            </text>
          </>
        ) : (
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: 15, fontWeight: 600, fill: 'var(--text)' }}
          >
            {formatBytes(model.totalBytes, lang)}
          </text>
        )}
      </svg>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10, minWidth: 200 }}>
        {model.segments.map((s) =>
          // The free slice is meaningless without a purchased volume.
          s.key === 'free' && !model.hasQuota ? null : (
            <li key={s.key} className="row" style={{ gap: 8, alignItems: 'baseline' }}>
              <span
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  flex: '0 0 auto',
                  background: s.key === 'burned' && over ? 'var(--danger)' : COLOR[s.key],
                  border: s.key === 'free' ? '1px solid var(--border, #d1d5db)' : undefined,
                }}
              />
              <span>
                <strong>{formatBytes(s.bytes, lang)}</strong>{' '}
                <span className="muted" style={{ fontSize: 13 }}>
                  {t(`quota.chart.${s.key}`)}
                </span>
              </span>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}
